import pako from 'pako';
import QRCode from 'qrcode';
import { alignDots } from './bitmap.js';
import { encodeCode128 } from './code128.js';

const PRINTER_DPI = 203;

/**
 * @param {string} zpl
 * @returns {boolean}
 */
function isValidZpl(zpl) {
    if (typeof zpl !== 'string') {
        return false;
    }
    const trimmed = zpl.trim();
    return trimmed.startsWith('^XA') && trimmed.endsWith('^XZ');
}

const COMMANDS = ['GFA', 'PW', 'LL', 'FO', 'FT', 'FB', 'FD', 'FS', 'FH', 'PQ', 'BQ', 'BC', 'BY', 'GB'];

/**
 * @param {string} body
 * @returns {string}
 */
function matchCommand(body) {
    for (let index = 0; index < COMMANDS.length; index += 1) {
        if (body.indexOf(COMMANDS[index]) === 0) {
            return COMMANDS[index];
        }
    }
    const font = body.match(/^A[0-9A-Z][NRIB]?/);
    return font ? font[0] : '';
}

/**
 * @param {string} zpl
 * @returns {{ code: string, args: string }[]}
 */
function tokenize(zpl) {
    const tokens = [];
    let index = 0;
    while (index < zpl.length) {
        const caret = zpl.indexOf('^', index);
        if (caret === -1) {
            break;
        }
        const command = matchCommand(zpl.slice(caret + 1));
        if (command === '') {
            index = caret + 1;
            continue;
        }
        const argsStart = caret + 1 + command.length;
        const next = zpl.indexOf('^', argsStart);
        tokens.push({
            code: command,
            args: zpl.slice(argsStart, next === -1 ? zpl.length : next)
        });
        index = next === -1 ? zpl.length : next;
    }
    return tokens;
}

/**
 * @param {string} args
 * @returns {number}
 */
function firstNumber(args, fallback) {
    const match = String(args).match(/\d+/);
    if (!match) {
        return fallback;
    }
    return parseInt(match[0], 10);
}

/**
 * @param {string} text
 * @returns {string}
 */
function decodeHexEscapes(text) {
    return text.replace(/\\([0-9A-Fa-f]{2})/g, function (all, hex) {
        return String.fromCharCode(parseInt(hex, 16));
    });
}

/**
 * @param {string} data
 * @returns {Uint8Array|null}
 */
function decodeZ64(data) {
    const marker = ':Z64:';
    const start = data.indexOf(marker);
    if (start === -1) {
        return null;
    }
    const payload = data.slice(start + marker.length);
    const end = payload.lastIndexOf(':');
    const b64 = end === -1 ? payload : payload.slice(0, end);
    const binary = atob(b64);
    const compressed = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
        compressed[index] = binary.charCodeAt(index) & 0xff;
    }
    return pako.inflate(compressed);
}

/**
 * @param {string} args
 * @returns {{ rowBytes: number, bytes: Uint8Array }|null}
 */
function decodeGfa(args) {
    const parts = args.split(',');
    const rowBytes = parseInt(parts[3], 10);
    if (!rowBytes) {
        return null;
    }
    const data = parts.slice(4).join(',');
    const bytes = decodeZ64(data);
    if (!bytes) {
        return null;
    }
    return { rowBytes: rowBytes, bytes: bytes };
}

/**
 * @returns {object}
 */
function createParseState() {
    return {
        fields: [],
        graphics: [],
        boxes: [],
        x: 0,
        y: 0,
        origin: 'fo',
        size: 30,
        blockWidth: 0,
        lines: 1,
        align: 'left',
        hexEscapes: false,
        widthDots: 440,
        heightDots: 232,
        copies: 1,
        fieldType: 'text',
        barcodeHeight: 50,
        printLine: true,
        moduleWidth: 2,
        qrMagnification: 2,
        qrLevel: 'M'
    };
}

/**
 * @param {object} state
 * @param {{ code: string, args: string }} token
 */
function applyToken(state, token) {
    if (token.code === 'PW') {
        state.widthDots = firstNumber(token.args, state.widthDots);
        return;
    }
    if (token.code === 'LL') {
        state.heightDots = firstNumber(token.args, state.heightDots);
        return;
    }
    if (token.code === 'PQ') {
        state.copies = firstNumber(token.args, 1);
        return;
    }
    if (token.code === 'FO' || token.code === 'FT') {
        const coords = token.args.match(/(\d+)\s*,\s*(\d+)/);
        if (coords) {
            state.x = parseInt(coords[1], 10);
            state.y = parseInt(coords[2], 10);
            state.origin = token.code === 'FT' ? 'ft' : 'fo';
        }
        return;
    }
    if (token.code.startsWith('A')) {
        state.size = firstNumber(token.args, state.size);
        return;
    }
    if (token.code === 'FB') {
        const parts = token.args.split(',');
        state.blockWidth = parseInt(parts[0], 10) || 0;
        state.lines = parseInt(parts[1], 10) || 1;
        state.align = (parts[3] || 'L').trim().toUpperCase() === 'R' ? 'right' : 'left';
        return;
    }
    if (token.code === 'FH') {
        state.hexEscapes = true;
        return;
    }
    if (token.code === 'BY') {
        state.moduleWidth = Math.max(1, firstNumber(token.args, state.moduleWidth));
        return;
    }
    if (token.code === 'BC') {
        const parts = token.args.split(',');
        state.fieldType = 'barcode';
        state.barcodeHeight = parseInt(parts[1], 10) || 50;
        state.printLine = (parts[2] || 'Y').trim().toUpperCase() !== 'N';
        return;
    }
    if (token.code === 'BQ') {
        const parts = token.args.split(',');
        const level = (parts[3] || 'M').trim().toUpperCase();
        state.fieldType = 'qrcode';
        state.qrMagnification = Math.max(1, parseInt(parts[2], 10) || 2);
        state.qrLevel = ['L', 'M', 'Q', 'H'].indexOf(level) === -1 ? 'M' : level;
        return;
    }
    if (token.code === 'GB') {
        pushBox(state, token.args);
        return;
    }
    if (token.code === 'GFA') {
        const graphic = decodeGfa(token.args);
        if (graphic) {
            state.graphics.push({ x: state.x, y: state.y, rowBytes: graphic.rowBytes, bytes: graphic.bytes });
        }
        return;
    }
    if (token.code === 'FD') {
        pushField(state, token.args);
    }
}

/**
 * @param {object} state
 * @param {string} args
 */
function pushBox(state, args) {
    const parts = args.split(',');
    const width = parseInt(parts[0], 10) || 0;
    const height = parseInt(parts[1], 10) || 0;
    if (width <= 0 || height <= 0) {
        return;
    }
    state.boxes.push({
        x: state.x,
        y: state.y,
        origin: state.origin,
        width: width,
        height: height,
        thickness: parseInt(parts[2], 10) || 1,
        color: (parts[3] || 'B').trim().toUpperCase()
    });
}

/**
 * @param {object} state
 * @param {string} args
 */
function pushField(state, args) {
    const text = state.hexEscapes ? decodeHexEscapes(args) : args;
    if (text !== '') {
        state.fields.push({
            type: state.fieldType,
            text: text,
            x: state.x,
            y: state.y,
            origin: state.origin,
            size: state.size,
            width: state.blockWidth,
            lines: state.lines,
            align: state.align,
            barcodeHeight: state.barcodeHeight,
            printLine: state.printLine,
            moduleWidth: state.moduleWidth,
            qrMagnification: state.qrMagnification,
            qrLevel: state.qrLevel
        });
    }
    state.blockWidth = 0;
    state.lines = 1;
    state.align = 'left';
    state.hexEscapes = false;
    state.fieldType = 'text';
}

/**
 * @param {string} zpl
 * @returns {{ widthDots: number, heightDots: number, copies: number, fields: object[], graphics: object[], boxes: object[] }}
 */
function parseZpl(zpl) {
    const state = createParseState();
    tokenize(zpl).forEach(function (token) {
        applyToken(state, token);
    });
    return {
        widthDots: state.widthDots,
        heightDots: state.heightDots,
        copies: state.copies > 0 ? state.copies : 1,
        fields: state.fields,
        graphics: state.graphics,
        boxes: state.boxes
    };
}

/**
 * @param {CanvasRenderingContext2D} context
 * @param {object} field
 * @param {number} scale
 */
function drawField(context, field, scale) {
    const fontSize = Math.max(1, Math.round(field.size * scale));
    const words = field.text.split(' ');
    const lines = [];
    let current = '';

    context.font = fontSize + 'px Helvetica, Arial, sans-serif';
    context.textBaseline = 'top';
    context.textAlign = field.align === 'right' ? 'right' : 'left';
    context.fillStyle = '#000000';

    const maxWidth = field.width > 0 ? field.width * scale : Infinity;
    words.forEach(function (word) {
        const next = current === '' ? word : current + ' ' + word;
        if (current !== '' && context.measureText(next).width > maxWidth) {
            lines.push(current);
            current = word;
            return;
        }
        current = next;
    });
    if (current !== '') {
        lines.push(current);
    }

    const anchorX = field.align === 'right' ? (field.x + field.width) * scale : field.x * scale;
    const shown = lines.slice(0, field.lines);
    if (field.origin === 'ft') {
        context.textBaseline = 'alphabetic';
        const last = shown.length - 1;
        shown.forEach(function (line, index) {
            context.fillText(line, anchorX, (field.y * scale) + ((index - last) * fontSize));
        });
        return;
    }
    shown.forEach(function (line, index) {
        context.fillText(line, anchorX, (field.y * scale) + (index * fontSize));
    });
}

/**
 * @param {CanvasRenderingContext2D} context
 * @param {object} field
 * @param {number} scale
 */
function drawBarcode(context, field, scale) {
    const bits = encodeCode128(field.text);
    const module = Math.max(1, field.moduleWidth) * scale;
    const barHeight = Math.max(1, field.barcodeHeight * scale);
    const textSize = Math.max(8, Math.round(barHeight * 0.28));
    let top = field.y * scale;
    if (field.origin === 'ft') {
        top -= barHeight;
        if (field.printLine) {
            top -= textSize;
        }
    }

    context.fillStyle = '#000000';
    for (let index = 0; index < bits.length; index += 1) {
        if (bits[index] === '1') {
            context.fillRect((field.x * scale) + (index * module), top, module, barHeight);
        }
    }
    if (!field.printLine) {
        return;
    }
    context.font = textSize + 'px Helvetica, Arial, sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'top';
    context.fillText(field.text, (field.x * scale) + ((bits.length * module) / 2), top + barHeight + 2);
}

/**
 * @param {CanvasRenderingContext2D} context
 * @param {object} field
 * @param {number} scale
 */
function drawQr(context, field, scale) {
    let qr;
    try {
        qr = QRCode.create(field.text, { errorCorrectionLevel: field.qrLevel || 'M' });
    } catch (error) {
        if (error) {
            return;
        }
    }
    const size = qr.modules.size;
    const module = Math.max(1, field.qrMagnification) * scale;
    let originX = field.x * scale;
    let originY = field.y * scale;
    if (field.origin === 'ft') {
        originY -= size * module;
    }
    context.fillStyle = '#000000';
    for (let row = 0; row < size; row += 1) {
        for (let col = 0; col < size; col += 1) {
            if (qr.modules.get(row, col)) {
                context.fillRect(originX + (col * module), originY + (row * module), module, module);
            }
        }
    }
}

/**
 * @param {CanvasRenderingContext2D} context
 * @param {object} box
 * @param {number} scale
 */
function drawBox(context, box, scale) {
    const width = box.width * scale;
    const height = box.height * scale;
    const thickness = Math.max(1, box.thickness * scale);
    const x = box.x * scale;
    let y = box.y * scale;
    if (box.origin === 'ft') {
        y -= height;
    }
    context.fillStyle = box.color === 'W' ? '#ffffff' : '#000000';
    if (thickness >= width || thickness >= height) {
        context.fillRect(x, y, width, height);
        return;
    }
    context.fillRect(x, y, width, thickness);
    context.fillRect(x, y + height - thickness, width, thickness);
    context.fillRect(x, y, thickness, height);
    context.fillRect(x + width - thickness, y, thickness, height);
}

/**
 * @param {CanvasRenderingContext2D} context
 * @param {object} graphic
 * @param {number} scale
 */
function drawGraphic(context, graphic, scale) {
    const width = graphic.rowBytes * 8;
    const height = Math.floor(graphic.bytes.length / graphic.rowBytes);
    context.fillStyle = '#000000';
    for (let row = 0; row < height; row += 1) {
        for (let col = 0; col < width; col += 1) {
            const byte = graphic.bytes[(row * graphic.rowBytes) + (col >> 3)];
            if ((byte >> (7 - (col & 7))) & 1) {
                context.fillRect((graphic.x + col) * scale, (graphic.y + row) * scale, scale, scale);
            }
        }
    }
}

/**
 * @param {number} width
 * @param {number} height
 * @returns {HTMLCanvasElement}
 */
function defaultCreateCanvas(width, height) {
    if (typeof document === 'undefined') {
        throw new Error('canvas_unavailable');
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
}

/**
 * @param {string} zpl
 * @param {{ dpi?: number, createCanvas?: function }} [options]
 * @returns {{ canvas: HTMLCanvasElement, copies: number, widthDots: number, heightDots: number }}
 */
function renderZpl(zpl, options) {
    const settings = options || {};
    const dpi = settings.dpi > 0 ? settings.dpi : PRINTER_DPI;
    const scale = PRINTER_DPI / dpi;
    const parsed = parseZpl(zpl);
    const widthDots = alignDots(Math.round(parsed.widthDots * scale));
    const heightDots = Math.max(1, Math.round(parsed.heightDots * scale));
    const createCanvas = settings.createCanvas || defaultCreateCanvas;
    const canvas = createCanvas(widthDots, heightDots);
    const context = canvas.getContext('2d');

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, widthDots, heightDots);
    parsed.graphics.forEach(function (graphic) {
        drawGraphic(context, graphic, scale);
    });
    parsed.boxes.forEach(function (box) {
        drawBox(context, box, scale);
    });
    parsed.fields.forEach(function (field) {
        if (field.type === 'barcode') {
            drawBarcode(context, field, scale);
            return;
        }
        if (field.type === 'qrcode') {
            drawQr(context, field, scale);
            return;
        }
        drawField(context, field, scale);
    });

    return {
        canvas: canvas,
        copies: parsed.copies,
        widthDots: widthDots,
        heightDots: heightDots
    };
}

export { isValidZpl, parseZpl, renderZpl, PRINTER_DPI };
